import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Search, RefreshCw, Users, UserRound, RadioTower, CreditCard, ExternalLink, ChevronDown, ChevronUp, Disc3, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [
  { key: "all", title: "All Accounts", icon: Users },
  { key: "nationals", title: "YaSHaRUN", icon: UserRound },
  { key: "subscribers", title: "Subscribers", icon: CreditCard },
  { key: "yasharal_live", title: "Yashar’al Live", icon: RadioTower },
  { key: "records", title: "Records / Artists", icon: Disc3 },
  { key: "publishing", title: "Publishing / Writers", icon: BookOpen },
];

const timeLabel = (value) => {
  if (!value) return "Not recorded";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};
const valueLabel = (value, absent = "Not recorded") =>
  value === null || value === undefined || value === "" ? absent : String(value);
const niceLabel = (text) => String(text || "").replace(/_/g, " ");
const categoryName = (value) => TABS.find((x) => x.key === value)?.title || niceLabel(value);
const Pill = ({ children }) => <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700">{children}</span>;

function Section({ title, children, subtitle }) {
  return <section className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 sm:p-5">
    <h3 className="text-base font-bold text-royal-blue">{title}</h3>
    {subtitle && <p className="mt-1 text-xs text-slate-500">{subtitle}</p>}
    <div className="mt-4">{children}</div>
  </section>;
}

function Table({ columns, rows, emptyText }) {
  if (!rows?.length) return <p className="rounded-md border border-dashed border-slate-200 p-4 text-sm text-slate-500">{emptyText || "No matching records stored."}</p>;
  return <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead><tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">{columns.map(col => <th key={col.key} className="pb-2 pr-4 font-semibold">{col.label}</th>)}</tr></thead>
      <tbody>{rows.map((row, i) => <tr key={row.id || row.order_id || row.key || i} className="border-b last:border-0">{columns.map(col => <td key={col.key} className="py-3 pr-4 align-top text-slate-700">{col.render ? col.render(row) : valueLabel(row[col.key])}</td>)}</tr>)}</tbody>
    </table>
  </div>;
}

export default function AdminAccountDirectory() {
  const [accounts, setAccounts] = useState([]);
  const [counts, setCounts] = useState({});
  const [plans, setPlans] = useState({});
  const [permissions, setPermissions] = useState({});
  const [tab, setTab] = useState("all");
  const [term, setTerm] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const detailSectionRef = useRef(null);
  const [error, setError] = useState("");
  const [grantingMusic, setGrantingMusic] = useState("");
  const [musicAccessError, setMusicAccessError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await axios.get(`${API}/admin/account-directory`);
      setAccounts(res.data.accounts || []);
      setCounts(res.data.counts || {});
      setPlans(res.data.plans || {});
      setPermissions(res.data.permissions || {});
    } catch (err) {
      if ([401, 403].includes(err?.response?.status)) {
        setError("This directory requires full user-management permission. Your current account may have narrower ministry-administration access.");
      } else setError(err?.response?.data?.detail || "Unable to load account records.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAccount = useCallback(async (id) => {
    setSelectedId(id); setDetailsLoading(true); setError(""); setShowAllHistory(false);
    setDetail(null);
    try {
      const res = await axios.get(`${API}/admin/account-directory/${encodeURIComponent(id)}`);
      setDetail(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load this account.");
    } finally { setDetailsLoading(false); }
  }, []);

  const grantMusicAccess = useCallback(async (area) => {
    if (!detail?.account?.id || grantingMusic) return;
    const person = detail.account;
    const name = person.display_name || person.username || "Artist / Writer";
    if (!window.confirm("Give " + name + " access to the " + (area === "records" ? "Record Label" : "Publishing") + " portal? This grants a workspace only; it does not sign an agreement or transfer rights.")) return;
    setGrantingMusic(area); setMusicAccessError("");
    try {
      await axios.post(`${API}/music/admin/${area}/access`, {
        user_id: person.id,
        display_name: name,
        test_access: false,
      });
      const [updatedAccount, updatedDirectory] = await Promise.all([
        axios.get(`${API}/admin/account-directory/${encodeURIComponent(person.id)}`),
        axios.get(`${API}/admin/account-directory`),
      ]);
      setDetail(updatedAccount.data);
      setAccounts(updatedDirectory.data.accounts || []);
      setCounts(updatedDirectory.data.counts || {});
    } catch (err) {
      setMusicAccessError(err?.response?.data?.detail || "Unable to assign the music workspace.");
    } finally { setGrantingMusic(""); }
  }, [detail, grantingMusic]);

  // After selecting an account on a phone/tablet, bring its full-width details
  // into view. Desktop visitors retain their own scroll position.
  useEffect(() => {
    if (!detail || !selectedId || typeof window === "undefined") return;
    if (window.matchMedia("(max-width: 1023px)").matches) {
      detailSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [detail, selectedId]);

  const rows = useMemo(() => accounts.filter(account => {
    const matchesTab = tab === "all" || (account.categories || []).includes(tab);
    const q = term.trim().toLowerCase();
    return matchesTab && (!q || [account.display_name, account.username, account.email, account.id]
      .some(value => String(value || "").toLowerCase().includes(q)));
  }), [accounts, tab, term]);

  return <div className="min-h-screen bg-parchment" data-testid="admin-account-directory">
    <header className="bg-royal-blue px-4 py-6 text-white sm:px-6 sm:py-7">
      <div className="mx-auto max-w-7xl">
        <Link to="/portal" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white"><ArrowLeft size={16}/> Portal</Link>
        <div className="mt-3 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div className="min-w-0"><h1 className="font-heading text-2xl font-bold sm:text-3xl">Accounts &amp; Memberships</h1>
            <p className="mt-2 text-sm text-white/80">One account per person, with separate Nation, Subscriber, Live, Records and Publishing access.</p>
          </div>
          <button className="inline-flex items-center justify-center gap-2 rounded-md border border-white/40 px-4 py-2 text-sm font-semibold hover:bg-white/10" onClick={load}>
            <RefreshCw size={15}/> Refresh
          </button>
        </div>
      </div>
    </header>
    <main className="mx-auto w-full max-w-7xl px-4 py-5 sm:px-6 sm:py-6">
      <div className="mb-5 flex flex-wrap gap-2">
        <Link className="text-sm font-semibold text-royal-blue underline" to="/nationals/admin">YaSHaRUN Administration</Link>
        <span className="text-slate-400">•</span>
        <Link className="text-sm font-semibold text-royal-blue underline" to="/admin/payments">Subscriber Payments &amp; Claims</Link>
        <span className="text-slate-400">•</span>
        <Link className="text-sm font-semibold text-royal-blue underline" to="/yasharal-live">Yashar’al Live</Link>
      </div>
      <p className="mb-4 text-sm text-slate-600">A person can appear in more than one category. These counts are overlapping views of the same master users collection, not additional user records.</p>
      {error && <div role="alert" className="mb-4 border-l-4 border-red-600 bg-red-50 p-4 text-sm text-red-900">{error}</div>}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
        {TABS.map(({ key, title, icon: Icon }) => <button key={key} onClick={() => { setTab(key); setSelectedId(null); setDetail(null); setShowAllHistory(false); }}
          aria-pressed={tab === key} data-testid={`directory-filter-${key}`}
          className={`rounded-lg border p-4 text-left ${tab === key ? "border-blue-700 bg-blue-50 ring-1 ring-blue-700" : "border-slate-200 bg-white hover:border-blue-300"}`}>
          <Icon size={20} className="mb-2 text-blue-700"/>
          <div className="text-sm font-semibold text-slate-700">{title}</div>
          <div className="mt-1 text-2xl font-bold text-royal-blue">{loading ? "…" : counts[key] ?? "—"}</div>
        </button>)}
      </div>
      <div className="mt-5 space-y-5">
        <Section title={`${categoryName(tab)} — Account Records`} subtitle="Select an account to see linked information. Names and access status come from the shared account record.">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-3 text-slate-400" size={18}/>
            <input value={term} onChange={e => setTerm(e.target.value)} aria-label="Search accounts"
              className="w-full rounded border border-slate-300 bg-white py-2.5 pl-10 pr-3 text-base" placeholder="Search name, email, username or account ID"/>
          </div>
          {loading ? <p className="text-sm text-slate-500">Loading accounts…</p> :
            rows.length === 0 ? <p className="text-sm text-slate-500">No matching accounts in this category.</p> :
            <div className="grid max-h-[360px] grid-cols-1 gap-3 overflow-y-auto overscroll-contain pr-1 sm:max-h-[440px] sm:grid-cols-2 lg:max-h-[420px] lg:grid-cols-3 xl:grid-cols-4" aria-label="Account selection list" tabIndex={0}>
              {rows.map(account => <button type="button" key={account.id} onClick={() => openAccount(account.id)}
                className={`min-w-0 w-full rounded-lg border p-3 text-left hover:border-blue-500 ${selectedId === account.id ? "border-blue-600 bg-blue-50" : "border-slate-200"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0"><div className="truncate font-semibold text-slate-800">{valueLabel(account.display_name, account.username || "Account")}</div>
                    <div className="truncate text-xs text-slate-500">@{account.username || "—"} · {account.email || "Email not provided"}</div>
                  </div>
                  <Pill>{account.status || "Unknown"}</Pill>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {(account.categories || []).map(cat => <Pill key={cat}>{categoryName(cat)}</Pill>)}
                  {Array.isArray(account.roles) && account.roles.includes("admin") && <Pill>Administrator</Pill>}
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  Subscriber: {account.subscription_tier || "Not subscribed"} · Live: {account.communications_plan || "No Live plan"} · Last login: {timeLabel(account.last_login_at)}
                </div>
              </button>)}
            </div>}
        </Section>
        <div ref={detailSectionRef} className="min-w-0 space-y-4 scroll-mt-4" tabIndex={-1}>
          {!selectedId && <Section title="Choose an account"><p className="text-sm text-slate-600">Select a user on the left to inspect memberships and linked records, including payment, room and recording history where authorized.</p></Section>}
          {detailsLoading && <Section title="Account details"><p className="text-sm text-slate-500">Loading the account’s records…</p></Section>}
          {detail && <><Section title={detail.account?.display_name || detail.account?.username || "Account"}>
            <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
              {[
                ["Account ID", detail.account?.id], ["Username", detail.account?.username],
                ["Email", detail.account?.email], ["Status", detail.account?.status],
                ["Created", timeLabel(detail.account?.created_at)], ["Last login", timeLabel(detail.account?.last_login_at)],
                ["YaSHaRUN status", detail.account?.national_status], ["Subscriber tier", detail.account?.subscription_tier || "Not subscribed"],
                ["Subscriber status", detail.account?.subscription_status || "Not subscribed"],
                ["Yashar’al Live plan", detail.account?.communications_plan || "No Live plan"],
                ["Live service status", detail.account?.communications_service_status || "Not activated"],
              ].map(([label, val]) => <div key={label}><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt><dd className="mt-0.5 break-words text-slate-800">{valueLabel(val)}</dd></div>)}
            </dl>
            <div className="mt-4 flex flex-wrap gap-2">{(detail.account?.categories || []).map(cat => <Pill key={cat}>{categoryName(cat)}</Pill>)}</div>
          </Section>
            {musicAccessError && <p role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{musicAccessError}</p>}
            <div className="grid gap-4 sm:grid-cols-2">
              <Section title="Records / Artist Access">
                {detail.music?.records ? <>
                  <p className="font-semibold text-slate-800">{detail.music.records.display_name || "Artist"}</p>
                  <p className="mt-1 text-sm text-slate-600">Access: {detail.music.records.status}</p>
                  <Link to="/music/records" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 underline">Open Record Label <ExternalLink size={14}/></Link>
                </> : <div>
                  <p className="text-sm text-slate-500">No Record Label access assigned.</p>
                  <button type="button" disabled={Boolean(grantingMusic)} onClick={() => grantMusicAccess("records")} className="mt-3 rounded-md border border-blue-300 px-3 py-2 text-sm font-semibold text-blue-800 disabled:opacity-50">Grant Record Label Access</button>
                </div>}
              </Section>
              <Section title="Publishing / Writer Access">
                {detail.music?.publishing ? <>
                  <p className="font-semibold text-slate-800">{detail.music.publishing.display_name || "Writer"}</p>
                  <p className="mt-1 text-sm text-slate-600">Access: {detail.music.publishing.status}</p>
                  <Link to="/music/publishing" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 underline">Open Publishing <ExternalLink size={14}/></Link>
                </> : <div>
                  <p className="text-sm text-slate-500">No Publishing access assigned.</p>
                  <button type="button" disabled={Boolean(grantingMusic)} onClick={() => grantMusicAccess("publishing")} className="mt-3 rounded-md border border-blue-300 px-3 py-2 text-sm font-semibold text-blue-800 disabled:opacity-50">Grant Publishing Access</button>
                </div>}
              </Section>
            </div>
            <Section title="YaSHaRUN records">
              {detail.national?.restricted ? <p className="text-sm text-slate-500">Application details require YaSHaRUN-application reading permission.</p> :
                <Table columns={[{key:"id",label:"Application ID"},{key:"status",label:"Status"},{key:"created_at",label:"Submitted",render:r=>timeLabel(r.created_at)}]}
                rows={detail.national?.applications} emptyText="No applications linked to this account ID."/>}
              <Link to="/admin/submissions" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 underline">Review YaSHaRUN applications <ExternalLink size={14}/></Link>
            </Section>
            <div className="grid items-start gap-4 xl:grid-cols-2">
            <Section title="Subscriber subscriptions &amp; orders">
              <div className="mb-4 rounded-md bg-blue-50 px-3 py-2 text-sm text-slate-700">
                <strong>Account access:</strong> {detail.subscriber?.account_access?.tier || "No Subscriber tier"} · {detail.subscriber?.account_access?.status || "No subscription status"}
                <div className="mt-1 text-xs text-slate-600">Account access is stored in All Accounts. Payment records are stored separately and only appear below when linked to this account ID.</div>
              </div>
              {detail.subscriber?.restricted ? <p className="text-sm text-slate-500">Subscription details require subscription-reading permission.</p> :
                <Table columns={[{key:"internal_plan_id",label:"Package / Plan",render:r=>r.internal_plan_id||r.plan||r.tier||"Not recorded"},{key:"subscription_status",label:"Subscription status",render:r=>r.subscription_status||r.status||"Not recorded"},{key:"claim_status",label:"Claim"}]}
                  rows={detail.subscriber?.subscriptions} emptyText="No separately linked Subscriber billing record found for this account ID. This does not mean its account-level access is inactive. Check Payments & Claims for unlinked or email-matched transactions."/>}
              {detail.subscriber?.orders_restricted ? <p className="mt-4 text-sm text-slate-500">Order history requires order-reading permission.</p> :
                <div className="mt-4"><h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-600">Orders</h4>
                <Table columns={[{key:"order_id",label:"Order"},{key:"payment_status",label:"Payment"},{key:"created_at",label:"Placed",render:r=>timeLabel(r.created_at)}]}
                  rows={detail.subscriber?.orders} emptyText="No orders linked by account ID. Check Payments & Claims for orders awaiting a claim or link."/></div>}
              {(detail.subscriber?.possible_unlinked_subscriptions?.length > 0 || detail.subscriber?.possible_unlinked_orders?.length > 0) && (
                <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3">
                  <h4 className="text-sm font-semibold text-amber-900">Possible unlinked payment records</h4>
                  <p className="mt-1 text-xs text-amber-900">
                    The purchaser email matches this account, but these records are not linked to a user ID.
                    This is not proof of ownership. Verify the Stripe transaction and identity before linking it in Payments &amp; Claims.
                  </p>
                  <div className="mt-3">
                    <Table columns={[{key:"internal_plan_id",label:"Possible subscription",render:r=>r.internal_plan_id||r.plan||r.tier||"Plan unknown"},{key:"subscription_status",label:"Status",render:r=>r.subscription_status||r.status||"Not recorded"}]}
                      rows={detail.subscriber?.possible_unlinked_subscriptions} emptyText="No unlinked subscriptions with this exact email."/>
                  </div>
                  <div className="mt-3">
                    <Table columns={[{key:"order_id",label:"Possible order"},{key:"payment_status",label:"Status"}]}
                      rows={detail.subscriber?.possible_unlinked_orders} emptyText="No unlinked orders with this exact email."/>
                  </div>
                </div>
              )}
              <Link to="/admin/payments" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 underline">Subscriber billing &amp; claims <ExternalLink size={14}/></Link>
            </Section>
            <Section title="Yashar’al Live service">
              <div className="mb-4 rounded-md bg-blue-50 px-3 py-2 text-sm text-slate-700">
                <strong>Live account access:</strong> {detail.yasharal_live?.account_access?.plan || "No Live plan"} · {detail.yasharal_live?.account_access?.status || "Not activated"}
                <div className="mt-1 text-xs text-slate-600">Free or administrator-granted access does not require a paid Live subscription record.</div>
              </div>
              {detail.yasharal_live?.restricted ? <p className="text-sm text-slate-500">Detailed Live records require protected administrator access.</p> : <>
              <p className="mb-3 text-xs font-bold uppercase text-slate-500">Live subscriptions</p>
              <Table columns={[
                {key:"plan",label:"Plan",render:r=>r.plan||r.communications_plan||"Not recorded"},
                {key:"status",label:"Status"},
                {key:"billing_cycle",label:"Billing"},
                {key:"launch_offer_applied",label:"Pricing",render:r=>r.launch_offer_applied&&r.launch_price_usd!=null?`Launch ${Number(r.launch_price_usd).toFixed(2)} × ${r.launch_price_months}, then ${Number(r.standard_monthly_price_usd||0).toFixed(2)}/mo`:(r.standard_monthly_price_usd!=null?`${Number(r.standard_monthly_price_usd).toFixed(2)}/mo`:"—")},
                {key:"created_at",label:"Started",render:r=>timeLabel(r.created_at)}
              ]}
                rows={detail.yasharal_live?.subscriptions} emptyText="No paid Live billing record linked to this account ID. The account may still have Free or manually granted Live access."/>
              <p className="mb-2 mt-4 text-xs font-bold uppercase text-slate-500">Studios and meeting rooms created</p>
              <Table columns={[{key:"title",label:"Room"},{key:"status",label:"Status"},{key:"created_at",label:"Created",render:r=>timeLabel(r.created_at)}]}
                rows={detail.yasharal_live?.rooms_created} emptyText="No rooms recorded for this account."/>
              <p className="mb-2 mt-4 text-xs font-bold uppercase text-slate-500">Recording records created</p>
              <Table columns={[{key:"title",label:"Recording"},{key:"room_id",label:"Room ID"},{key:"recorded_at",label:"Recorded",render:r=>timeLabel(r.recorded_at)}]}
                rows={detail.yasharal_live?.recordings_created} emptyText="No recordings linked to this account."/>
              </>}
            </Section>
            </div>
            <Section
              title="Account administration history"
              subtitle="Newest first. This history records account actions, not application decisions; older entries remain preserved."
            >
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-slate-600">
                  {detail.account_history?.length || 0} recent recorded action{detail.account_history?.length === 1 ? "" : "s"}
                  {detail.account_history?.length > 8 && !showAllHistory ? " · showing latest 8" : ""}
                </p>
                {(detail.account_history?.length || 0) > 8 && (
                  <button
                    type="button"
                    aria-expanded={showAllHistory}
                    aria-controls="account-administration-history-rows"
                    onClick={() => setShowAllHistory(value => !value)}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-blue-700 hover:bg-blue-50"
                  >
                    {showAllHistory ? <>Show recent only <ChevronUp size={15}/></> : <>View all loaded history <ChevronDown size={15}/></>}
                  </button>
                )}
              </div>
              <div
                id="account-administration-history-rows"
                className={showAllHistory ? "max-h-[420px] overflow-y-auto overscroll-contain rounded-md border border-slate-200 p-2" : ""}
                tabIndex={showAllHistory ? 0 : undefined}
                aria-label="Account administration history records"
              >
                <Table
                  columns={[
                    {key:"action_label",label:"Action"},
                    {key:"performed_by",label:"Performed by"},
                    {key:"created_at",label:"When",render:r=>timeLabel(r.created_at)}
                  ]}
                  rows={showAllHistory ? detail.account_history : detail.account_history?.slice(0, 8)}
                  emptyText="No account administration actions found for this account."
                />
              </div>
              {(detail.account_history?.length || 0) >= 40 && (
                <p className="mt-2 text-xs text-slate-500">
                  This panel shows up to 40 recent actions. Earlier records remain in MongoDB and are not deleted.
                </p>
              )}
            </Section>
          </>}
        </div>
      </div>
      <div className="mt-5 grid min-w-0 items-start gap-4 lg:grid-cols-2">
        {permissions.billing && <Section title="Subscriber packages" subtitle="From gov_store.packages, not Yashar’al Live billing.">
          <Table columns={[{key:"name",label:"Package",render:r=>r.name||r.title||r.tier||r.key||r.id||"Unnamed"},{key:"monthly_price_usd",label:"Monthly (USD)",render:r=>r.monthly_price_usd != null ? `${Number(r.monthly_price_usd).toFixed(2)}` : "Contact administration"}]}
          rows={plans.subscriber} emptyText="No subscriber packages returned."/>
        </Section>}
        {permissions.live_details && <Section title="Yashar’al Live packages" subtitle="Canonical package catalog from yasharal_live.packages.">
          <Table columns={[
            {key:"name",label:"Package",render:r=>r.name||r.title||r.key||r.id||"Unnamed"},
            {key:"monthly_price_usd",label:"Normal monthly",render:r=>r.monthly_price_usd != null ? `${Number(r.monthly_price_usd).toFixed(2)}` : "Custom quote"},
            {key:"launch_price_usd",label:"Founding monthly",render:r=>r.launch_offer_available&&r.launch_price_usd!=null?`${Number(r.launch_price_usd).toFixed(2)} × ${r.launch_price_months} billing cycles`:(r.monthly_price_usd===0?"Free":"—")},
            {key:"annual_price_usd",label:"Normal annual",render:r=>r.annual_price_usd != null ? `${Number(r.annual_price_usd).toFixed(2)} · ${r.annual_discount_percent}% off monthly` : "—"},
            {key:"launch_annual_price_usd",label:"Founding annual",render:r=>r.launch_offer_available&&r.launch_annual_price_usd!=null?`${Number(r.launch_annual_price_usd).toFixed(2)} first year · ${r.launch_annual_discount_percent}% off annual`:"—"}
          ]}
          rows={plans.yasharal_live} emptyText="No Yashar’al Live packages returned."/>
        </Section>}
      </div>
    </main>
  </div>;
}
